import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AREAS, skillId } from "@/lib/areas";
import { CoachAvatar } from "@/components/ui/CoachAvatar";
import { ExerciseCoachCard } from "@/components/ui/ExerciseDemo";
import { ArrowLeft, ArrowRight, Check, Plus, Star, Trophy, Flame, Users, Mic, Dumbbell } from "lucide-react";

const LEVELS = { beginner: "#5CE1A6", intermediate: "#FFB340", advanced: "#FF6B6B" };

function useAreaData() {
  const { user } = useAuth();
  const { data: profile } = useQuery({ queryKey: ["training-profile"], queryFn: async () => (await api.get("/api/profile/training")).data });
  const { data: club } = useQuery({ queryKey: ["club-mine"], queryFn: async () => (await api.get("/api/club/mine")).data, enabled: !!user?.club_id });
  return { user, profile, club };
}

function progressFor(area, prefs) {
  const done = new Set(prefs?.skills || []);
  const n = area.skills.filter((_, i) => done.has(skillId(area.key, i))).length;
  return { n, total: area.skills.length, pct: Math.round((100 * n) / area.skills.length) };
}

export default function Areas() {
  const navigate = useNavigate();
  const { user, profile, club } = useAreaData();
  const { refresh } = useAuth();
  const followed = new Set(user?.prefs?.areas || []);
  const clubAreas = new Set(club?.areas || []);
  const toggle = async (key) => {
    const next = followed.has(key) ? [...followed].filter((k) => k !== key) : [...followed, key];
    try { await api.patch("/api/me/prefs", { areas: next }); await refresh(); } catch (e) { toast.error(apiErr(e)); }
  };
  const radar = Object.fromEntries((profile?.radar || []).map((r) => [r.pattern, r.score]));
  const sorted = [...AREAS].sort((a, b) => (followed.has(b.key) || clubAreas.has(b.key) ? 1 : 0) - (followed.has(a.key) || clubAreas.has(a.key) ? 1 : 0));

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="relative overflow-hidden rounded-2xl border border-[#1E222A] min-h-[16rem] flex items-end">
        <img src={AREAS[3].img} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg,#050505 15%,rgba(5,5,5,0.5) 65%,rgba(5,5,5,0.2))" }} />
        <div className="relative p-6 md:p-10 max-w-2xl">
          <span className="mono-label text-acid">crossfit · training areas</span>
          <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tight mt-1">Seven areas. One complete athlete.</h1>
          <p className="text-[#B7BECB] mt-3">Follow the areas you want to focus on. Each one has its own programmes, skill ladder, benchmarks and warm-up, and your OCTON coach plans around them.</p>
          {club?.areas?.length > 0 && <p className="mt-3 text-sm" style={{ color: club.color }}><Users className="inline w-4 h-4 mr-1" />{club.name} focuses on: {club.areas.map((k) => AREAS.find((a) => a.key === k)?.title).filter(Boolean).join(", ")}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {sorted.map((a, i) => {
          const prog = progressFor(a, user?.prefs);
          const isF = followed.has(a.key);
          return (
            <motion.div key={a.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} whileHover={{ y: -5 }}
              className="group relative rounded-2xl overflow-hidden border bg-[#0F1115] flex flex-col" style={{ borderColor: isF ? `${a.c}88` : "#1E222A" }} data-testid={`area-${a.key}`}>
              <button onClick={() => navigate(`/app/areas/${a.key}`)} className="relative h-44 overflow-hidden text-left">
                <img src={a.img} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0" style={{ background: `linear-gradient(0deg,#0F1115 5%,rgba(15,17,21,0.2) 70%), linear-gradient(135deg, ${a.c}33, transparent 60%)` }} />
                <div className="absolute top-3 left-3 flex gap-1.5">
                  {isF && <span className="mono-label rounded-full px-2 py-0.5 text-black" style={{ background: a.c }}>following</span>}
                  {clubAreas.has(a.key) && <span className="mono-label rounded-full px-2 py-0.5 bg-black/70 border border-white/20">club focus</span>}
                </div>
                <div className="absolute bottom-3 left-4 right-4">
                  <h3 className="font-heading font-black text-2xl tracking-tight">{a.title}</h3>
                  <p className="text-sm text-[#D7DCE5]">{a.tagline}</p>
                </div>
              </button>
              <div className="p-4 flex flex-col gap-3 flex-1">
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg bg-[#0B0D11] border border-[#1E222A] p-2.5">
                    <p className="mono-label">skills</p>
                    <p className="font-heading font-black text-lg">{prog.n}/{prog.total}</p>
                    <div className="h-1 rounded-full bg-[#1E222A] mt-1 overflow-hidden"><div className="h-full" style={{ width: `${prog.pct}%`, background: a.c }} /></div>
                  </div>
                  <div className="rounded-lg bg-[#0B0D11] border border-[#1E222A] p-2.5">
                    <p className="mono-label">your score</p>
                    <p className="font-heading font-black text-lg">{radar[a.radar] ?? "–"}</p>
                    <p className="text-[10px] text-[#8A91A6]">from logged training</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-auto">
                  <button onClick={() => toggle(a.key)} className="flex-1 h-10 rounded-full border text-sm inline-flex items-center justify-center gap-1.5"
                    style={isF ? { background: a.c, color: "#050505", borderColor: a.c } : { borderColor: "#343A4A" }}>
                    {isF ? <><Check className="w-4 h-4" /> Following</> : <><Plus className="w-4 h-4" /> Follow</>}
                  </button>
                  <button onClick={() => navigate(`/app/areas/${a.key}`)} className="flex-1 h-10 rounded-full bg-white text-black text-sm font-medium inline-flex items-center justify-center gap-1.5 hover:bg-acid">
                    Open <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

export function AreaDetail() {
  const { key } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const area = AREAS.find((a) => a.key === key) || AREAS[0];
  const { data: workouts = [] } = useQuery({ queryKey: ["workouts"], queryFn: async () => (await api.get("/api/workouts")).data });
  const { data: prs = [] } = useQuery({ queryKey: ["prs"], queryFn: async () => (await api.get("/api/logs/prs")).data });
  const [busy, setBusy] = useState(false);
  const done = new Set(user?.prefs?.skills || []);
  const following = (user?.prefs?.areas || []).includes(area.key);
  const plans = useMemo(() => {
    const seen = new Set();
    return workouts.filter((w) => area.focus.includes(w.focus) && !seen.has(w.name) && seen.add(w.name));
  }, [workouts, area]);
  const prMap = Object.fromEntries(prs.map((p) => [p.movement.toLowerCase(), p]));
  const prog = progressFor(area, user?.prefs);

  const tick = async (i) => {
    const id = skillId(area.key, i);
    const next = done.has(id) ? [...done].filter((x) => x !== id) : [...done, id];
    setBusy(true);
    try { await api.patch("/api/me/prefs", { skills: next }); await refresh(); if (!done.has(id)) toast.success("Skill unlocked. Nice work!"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const follow = async () => {
    const cur = user?.prefs?.areas || [];
    try { await api.patch("/api/me/prefs", { areas: following ? cur.filter((k) => k !== area.key) : [...cur, area.key] }); await refresh(); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <button onClick={() => navigate("/app/areas")} className="text-sm text-[#8A91A6] hover:text-white inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> All training areas</button>

      <div className="relative overflow-hidden rounded-2xl border min-h-[18rem] flex items-end" style={{ borderColor: `${area.c}55` }}>
        <img src={area.img} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(90deg,#050505 15%,rgba(5,5,5,0.55) 65%,rgba(5,5,5,0.15)), linear-gradient(0deg, ${area.c}22, transparent)` }} />
        <div className="relative p-6 md:p-10 w-full flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <span className="mono-label" style={{ color: area.c }}>training area</span>
            <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tight mt-1">{area.title}</h1>
            <p className="text-[#D7DCE5] mt-3">{area.why}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={follow} className="h-11 px-5 rounded-full font-medium inline-flex items-center gap-2" style={following ? { background: area.c, color: "#050505" } : { border: `1px solid ${area.c}`, color: area.c }}>
              {following ? <><Check className="w-4 h-4" /> Following</> : <><Plus className="w-4 h-4" /> Follow this area</>}
            </button>
            <button onClick={() => navigate(`/app/coach?topic=${area.key}`)} className="h-11 px-5 rounded-full bg-white text-black font-medium inline-flex items-center gap-2 hover:bg-acid"><Mic className="w-4 h-4" /> Ask your coach</button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Skill ladder */}
        <div className="lg:col-span-5 rounded-xl border border-[#1E222A] bg-[#0F1115] p-5" data-testid="skill-ladder">
          <div className="flex items-center justify-between">
            <p className="font-heading font-bold text-lg flex items-center gap-2"><Star className="w-4 h-4" style={{ color: area.c }} /> Skill ladder</p>
            <span className="mono-label">{prog.n} of {prog.total}</span>
          </div>
          <div className="h-1.5 rounded-full bg-[#1E222A] overflow-hidden mt-2"><motion.div initial={{ width: 0 }} animate={{ width: `${prog.pct}%` }} className="h-full" style={{ background: area.c }} /></div>
          <ol className="mt-4 relative border-l border-[#1E222A] ml-2 space-y-3">
            {area.skills.map(([lvl, name], i) => {
              const ok = done.has(skillId(area.key, i));
              return (
                <li key={name} className="pl-5 relative">
                  <button disabled={busy} onClick={() => tick(i)} aria-label={ok ? "Mark not done" : "Mark done"}
                    className="absolute -left-[11px] top-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center"
                    style={ok ? { background: area.c, borderColor: area.c } : { borderColor: "#343A4A", background: "#0F1115" }}>
                    {ok && <Check className="w-3 h-3 text-black" />}
                  </button>
                  <p className={`text-sm ${ok ? "text-white" : "text-[#B7BECB]"}`}>{name}</p>
                  <p className="mono-label" style={{ color: LEVELS[lvl] }}>{lvl}</p>
                </li>
              );
            })}
          </ol>
          <p className="text-xs text-[#565C6F] mt-4">Tick a skill when you've achieved it. Your coach uses this to pitch your training at the right level.</p>
        </div>

        <div className="lg:col-span-7 space-y-5">
          {/* Benchmarks */}
          {area.benchmarks.length > 0 && (
            <div className="rounded-xl border border-[#1E222A] bg-[#0F1115] p-5">
              <p className="font-heading font-bold text-lg flex items-center gap-2"><Trophy className="w-4 h-4 text-[#FFD24A]" /> Your benchmarks</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                {area.benchmarks.map((b) => {
                  const p = prMap[b.toLowerCase()];
                  const val = p ? (p.best_est_1rm ? `${p.best_est_1rm} kg` : p.best_weight_kg ? `${p.best_weight_kg} kg` : p.best_time_sec ? `${Math.floor(p.best_time_sec / 60)}:${String(p.best_time_sec % 60).padStart(2, "0")}` : p.best_score || "logged") : null;
                  return (
                    <div key={b} className="rounded-lg border p-3" style={{ borderColor: p ? `${area.c}55` : "#1E222A", background: p ? `${area.c}0D` : "#0B0D11" }}>
                      <p className="text-sm font-medium truncate">{b}</p>
                      <p className="font-heading font-black text-xl" style={{ color: p ? area.c : "#565C6F" }}>{val || "not yet"}</p>
                      {p?.best_est_1rm && <p className="mono-label">est. 1-rep max</p>}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-[#565C6F] mt-3">Tell your coach a result (e.g. "Fran in 6:42" or "deadlift 140 for 3") and it lands here automatically.</p>
            </div>
          )}

          {/* Programmes */}
          <div className="rounded-xl border border-[#1E222A] bg-[#0F1115] p-5">
            <p className="font-heading font-bold text-lg flex items-center gap-2"><Dumbbell className="w-4 h-4" style={{ color: area.c }} /> Programmes</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              {plans.map((w) => (
                <div key={w.id} className="rounded-lg border border-[#1E222A] bg-[#0B0D11] p-3">
                  <p className="font-medium">{w.name}</p>
                  <p className="text-xs text-[#8A91A6] mt-0.5">{w.description}</p>
                  <div className="mt-2 space-y-0.5">
                    {(w.days?.[0]?.exercises || []).slice(0, 4).map((e, j) => (
                      <div key={j} className="flex justify-between text-xs"><span className="text-white/80">{e.exercise}</span><span className="text-[#8A91A6]">{e.sets}×{e.reps}</span></div>
                    ))}
                  </div>
                </div>
              ))}
              {plans.length === 0 && <p className="text-sm text-[#565C6F]">Programmes for this area are on the way. Ask your coach for a session in the meantime.</p>}
            </div>
            <button onClick={() => navigate("/app/workouts")} className="mt-3 text-sm text-acid inline-flex items-center gap-1">Log a session from Workouts <ArrowRight className="w-3.5 h-3.5" /></button>
          </div>

          {/* Technique */}
          {area.demos?.length > 0 && <Technique area={area} />}

          {/* Warm-up + coach */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button onClick={() => navigate(area.link || "/app/warmup")} className="rounded-xl border border-[#1E222A] bg-[#0F1115] p-4 text-left hover:border-[#FFB340]">
              <Flame className="w-5 h-5 text-[#FFB340]" />
              <p className="font-heading font-bold mt-2">Warm up for {area.title.toLowerCase()}</p>
              <p className="text-xs text-[#8A91A6]">The right 6 to 10 minute routine before you start.</p>
            </button>
            <button onClick={() => navigate("/app/diary")} className="rounded-xl border border-[#1E222A] bg-[#0F1115] p-4 text-left hover:border-acid flex gap-3">
              <CoachAvatar size={36} ring={false} />
              <div><p className="font-heading font-bold">Plan it into your week</p><p className="text-xs text-[#8A91A6]">Your coach fits {area.title.toLowerCase()} sessions around your diary and cycle.</p></div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function Technique({ area }) {
  const [sel, setSel] = useState(area.demos[0]);
  return (
    <div className="rounded-xl border border-[#1E222A] bg-[#0F1115] p-5" data-testid="technique">
      <p className="font-heading font-bold text-lg">Technique</p>
      <p className="text-sm text-[#8A91A6]">Watch the movement, then scrub frame by frame or slow it to a quarter speed. The dotted line is the bar path.</p>
      <div className="flex flex-wrap gap-1.5 my-3">
        {area.demos.map((d) => (
          <button key={d} onClick={() => setSel(d)} className="px-3 h-8 rounded-full text-sm border"
            style={sel === d ? { background: area.c, color: "#050505", borderColor: area.c } : { borderColor: "#343A4A", color: "#B7BECB" }}>{d}</button>
        ))}
      </div>
      <ExerciseCoachCard key={sel} name={sel} accent={area.c} />
    </div>
  );
}
